import { FormEvent, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowRight, CircleCheck, Loader2 } from "lucide-react";
import AuthSplit from "layouts/auth/types/AuthSplit";
import { Button } from "components/ui/Button";
import env from "variables/config";
import { AuthHeading, FormAlert, linkClass, PasswordField } from "views/auth/authParts";

function ResetPassword() {
    const navigate = useNavigate();
    const { id: token } = useParams();
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);
    const [passwordError, setPasswordError] = useState<string | null>(null);
    const [confirmError, setConfirmError] = useState<string | null>(null);
    // The link is single-use: once it fails the form can't succeed, so we offer a new one instead.
    const [linkInvalid, setLinkInvalid] = useState(false);
    const [formError, setFormError] = useState<string | null>(null);

    const submit = (e: FormEvent) => {
        e.preventDefault();
        const nextPasswordError = password ? null : "Choisissez un nouveau mot de passe.";
        const nextConfirmError = !confirmPassword
            ? "Confirmez votre nouveau mot de passe."
            : confirmPassword !== password
            ? "Les deux mots de passe ne correspondent pas."
            : null;
        setPasswordError(nextPasswordError);
        setConfirmError(nextConfirmError);
        setFormError(null);
        if (nextPasswordError || nextConfirmError) return;

        setLoading(true);
        axios
            .post(`${env.apiUrl}/auth/reset-password`, { token, password })
            .then(() => setDone(true))
            .catch((error: any) => {
                if (error?.response?.status === 404) setLinkInvalid(true);
                else setFormError("Modification impossible pour le moment. Réessayez dans quelques instants.");
            })
            .finally(() => setLoading(false));
    };

    if (done) {
        return (
            <AuthSplit>
                <div className="mb-6 inline-flex h-12 w-12 items-center justify-center rounded-full bg-success-subtle text-success">
                    <CircleCheck className="h-5 w-5" />
                </div>
                <AuthHeading title="Mot de passe modifié" subtitle="Votre nouveau mot de passe est actif. Vous pouvez vous connecter." />
                <Button variant="accent" size="lg" className="w-full" onClick={() => navigate("/auth/login")}>
                    Se connecter
                    <ArrowRight className="h-4 w-4" />
                </Button>
            </AuthSplit>
        );
    }

    return (
        <AuthSplit>
            <AuthHeading title="Nouveau mot de passe" subtitle="Choisissez le mot de passe que vous utiliserez pour vous connecter." />

            {linkInvalid && (
                <FormAlert>
                    Ce lien n'est plus valide : il a déjà servi ou a été remplacé par un plus récent.{" "}
                    <Link to="/auth/forgot-password" className="font-medium underline underline-offset-4">
                        Demander un nouveau lien
                    </Link>
                </FormAlert>
            )}
            {formError && <FormAlert>{formError}</FormAlert>}

            <form onSubmit={submit} noValidate className="space-y-4">
                <PasswordField
                    id="password"
                    label="Nouveau mot de passe"
                    autoComplete="new-password"
                    autoFocus
                    placeholder="Votre nouveau mot de passe"
                    value={password}
                    error={passwordError}
                    onChange={(e) => {
                        setPassword(e.target.value);
                        setPasswordError(null);
                        setFormError(null);
                    }}
                />

                <PasswordField
                    id="confirmPassword"
                    label="Confirmation"
                    autoComplete="new-password"
                    placeholder="Saisissez-le à nouveau"
                    value={confirmPassword}
                    error={confirmError}
                    onChange={(e) => {
                        setConfirmPassword(e.target.value);
                        setConfirmError(null);
                        setFormError(null);
                    }}
                />

                <Button type="submit" variant="accent" size="lg" disabled={loading || linkInvalid} aria-busy={loading} className="w-full">
                    {loading ? (
                        <>
                            <Loader2 className="h-4 w-4 motion-safe:animate-spin" />
                            Enregistrement…
                        </>
                    ) : (
                        <>
                            Enregistrer le mot de passe
                            <ArrowRight className="h-4 w-4" />
                        </>
                    )}
                </Button>
            </form>

            <p className="mt-6 text-center text-sm text-ink-muted">
                <Link to="/auth/login" className={linkClass}>
                    Retour à la connexion
                </Link>
            </p>
        </AuthSplit>
    );
}

export default ResetPassword;
